"use client"

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

import { productPersistKey } from "@/stores/app-store"
import { clearSurveysOnboardingProgress } from "@/lib/evaluation-dashboard-onboarding"
import {
  isEvaluationDashboardScenario,
  type EvaluationDashboardScenario,
} from "@/lib/evaluation-dashboard-scenario"

const STORAGE_KEY = productPersistKey("exxat-surveys", "dashboard-scenario")
const VERSION = 3
const DEFAULT_SCENARIO: EvaluationDashboardScenario = "onboarding"

const listeners = new Set<() => void>()
let snapshot: EvaluationDashboardScenario | null = null
let hydrated = false

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function read(): EvaluationDashboardScenario {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (typeof parsed === "object" && parsed !== null) {
        const version = (parsed as { v?: number }).v
        const data = (parsed as { d?: unknown }).d
        if (version === VERSION && isEvaluationDashboardScenario(data)) {
          snapshot = data
          return snapshot
        }
      }
    } catch {
      // fall through
    }
  }

  snapshot = DEFAULT_SCENARIO
  if (raw) {
    clearSurveysOnboardingProgress()
    setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: snapshot }))
  }
  return snapshot
}

export function setEvaluationDashboardScenario(next: EvaluationDashboardScenario) {
  if (next === "onboarding") {
    clearSurveysOnboardingProgress()
  }
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

export function useEvaluationDashboardScenario(): EvaluationDashboardScenario {
  return React.useSyncExternalStore(subscribe, read, () => DEFAULT_SCENARIO)
}
